import React, { useState } from 'react';
import { CreditPack } from '../../types';
import { Button, Card } from '../common';
import { creditsApi } from '../../api/credits';
import { trackEvent } from '../../lib/analytics';

interface CreditPackCardProps {
  pack: CreditPack;
  onPurchaseStart?: () => void;
  onPurchaseError?: (error: Error) => void;
}

export default function CreditPackCard({
  pack,
  onPurchaseStart,
  onPurchaseError,
}: CreditPackCardProps) {
  const [loading, setLoading] = useState(false);

  const handleBuy = async () => {
    setLoading(true);
    onPurchaseStart?.();
    trackEvent('checkout_started', { pack_id: pack.id });

    try {
      const successUrl = `${window.location.origin}/credits?success=true`;
      const cancelUrl = `${window.location.origin}/credits?canceled=true`;

      const response = await creditsApi.createCheckout(
        pack.id,
        successUrl,
        cancelUrl
      );

      // Backend returns the full checkout URL, redirect directly
      if (response.sessionUrl) {
        window.location.href = response.sessionUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      onPurchaseError?.(error as Error);
    } finally {
      setLoading(false);
    }
  };

  const priceDisplay = pack.priceDisplay || `${(pack.pricePence / 100).toFixed(2)}`;

  return (
    <Card
      className={`relative ${pack.popular ? 'border-primary' : ''}`}
    >
      {pack.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-primary text-primary-foreground font-mono text-xs tracking-widest uppercase px-3 py-1 rounded-sm">
            Most popular
          </span>
        </div>
      )}

      <div className={`text-center ${pack.popular ? 'pt-3' : ''}`}>
        <h3 className="font-display text-lg font-medium text-foreground">{pack.name}</h3>

        <div className="mt-4">
          <span className="font-display text-4xl font-normal text-foreground">
            ${priceDisplay}
          </span>
        </div>

        <div className="mt-2">
          <span className="font-mono text-2xl font-medium text-primary">
            {pack.credits}
          </span>
          <span className="font-body text-muted-foreground ml-1">credits</span>
        </div>

        {pack.description && (
          <p className="mt-3 font-body text-sm text-muted-foreground">{pack.description}</p>
        )}

        <div className="mt-2 font-mono text-xs text-muted-foreground">
          {(pack.pricePence / pack.credits / 100).toFixed(2)} per credit
        </div>

        <Button
          onClick={handleBuy}
          loading={loading}
          variant={pack.popular ? 'primary' : 'secondary'}
          className="mt-6 w-full"
        >
          Buy credits
        </Button>
      </div>
    </Card>
  );
}
